import { datadogLogs } from "@datadog/browser-logs";
import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { csvEscapeField } from "@redotech/redo-common-model-csv-common/escape-field";
import { CustomOrderImportSchema } from "@redotech/redo-fulfillment-common/import-schema";
import ArrowRightIcon from "@redotech/redo-icons/arrow-right_filled.svg";
import { RedoMerchantRpcClientContext } from "@redotech/redo-merchant-app-common/rpc-client";
import { Provider } from "@redotech/redo-order-common/provider";
import { toast } from "@redotech/redo-web/alert";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoFileUpload } from "@redotech/redo-web/arbiter-components/file-upload/redo-file-upload";
import { RedoBaseModal } from "@redotech/redo-web/arbiter-components/modal/redo-base-modal";
import { RedoModalHeader } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { TeamContext } from "@redotech/redo-web/context/team-context";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { assertNever } from "@redotech/util/type";
import classNames from "classnames";
import { memo, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { RedoOutboundLabelsRpcClientContext } from "../outbound-labels-rpc-client";
import {
  downloadCSV,
  generateCSVContent,
  getCSVExample,
  getCustomSchemaCSVExample,
} from "../utils/csv-examples";
import {
  adultsAndCraftsColumnMap,
  parseAdultsAndCraftsCsv,
} from "../utils/parse-adults-and-crafts-csv";
import {
  apparelMagicColumnMap,
  parseApparelMagicCsv,
} from "../utils/parse-apparel-magic-csv";
import { parseCustomImportCsv } from "../utils/parse-custom-import-csv";
import {
  fortyBoxesColumnMap,
  parseFortyBoxesCsv,
} from "../utils/parse-forty-boxes-csv";
import {
  kickstarterColumnMap,
  parseKickstarterCsv,
} from "../utils/parse-kickstarter-csv";
import {
  FieldToColumnMap,
  formatParseErrors,
  formatValidationErrors,
  validateFulfillmentOrders,
} from "../utils/validate-fulfillment-orders";
import * as importOrdersModalCss from "./import-orders-modal.module.css";

const ADULTS_AND_CRAFTS_TEAM_ID = "696a6981b96183779956626b"; // TEMP UNTIL WE ALLOW CSV MAPPING
const TYSON_DEV_3_TEAM_ID = "699cba45b3f2e98984650e8c";

export enum FulfillmentImportSource {
  ETSY = "etsy",
  WOO_COMMERCE = "woo-commerce",
  BIG_COMMERCE = "big-commerce",
  WIX = "wix",
  SQUARESPACE = "squarespace",
  APPAREL_MAGIC = "apparel-magic",
  FORTY_BOXES = "forty-boxes",
  KICKSTARTER = "kickstarter",
  ADULTS_AND_CRAFTS = "adults-and-crafts",
}

const getFulfillmentInputSourceDisplayName = (
  fulfillmentSource: FulfillmentImportSource,
) => {
  switch (fulfillmentSource) {
    case FulfillmentImportSource.ETSY:
      return "Etsy";
    case FulfillmentImportSource.WOO_COMMERCE:
      return "WooCommerce";
    case FulfillmentImportSource.BIG_COMMERCE:
      return "BigCommerce";
    case FulfillmentImportSource.WIX:
      return "Wix";
    case FulfillmentImportSource.SQUARESPACE:
      return "Squarespace";
    case FulfillmentImportSource.APPAREL_MAGIC:
      return "ApparelMagic";
    case FulfillmentImportSource.FORTY_BOXES:
      return "40 Boxes";
    case FulfillmentImportSource.KICKSTARTER:
      return "Kickstarter";
    case FulfillmentImportSource.ADULTS_AND_CRAFTS:
      return "Adults & Crafts";
    default:
      assertNever(fulfillmentSource);
  }
};

type SelectedImportSource =
  | { type: "builtin"; source: FulfillmentImportSource }
  | { type: "custom"; schema: CustomOrderImportSchema };

export const ImportOrdersModal = memo(function ImportOrdersModal({
  open,
  setOpen,
  triggerReload,
}: {
  open: boolean;
  setOpen: (val: boolean) => void;
  triggerReload: () => Promise<void>;
}) {
  const [selectedSource, setSelectedSource] = useState<
    SelectedImportSource | undefined
  >(undefined);

  const [importLoading, setImportLoading] = useState(false);
  const [customSchemas, setCustomSchemas] = useState<CustomOrderImportSchema[]>(
    [],
  );

  const cancelImport = useHandler(() => {
    setOpen(false);
  });

  const client = useRequiredContext(RedoOutboundLabelsRpcClientContext);
  const merchantClient = useRequiredContext(RedoMerchantRpcClientContext);

  useEffect(() => {
    if (!open) {
      return;
    }

    const loadSchemas = async () => {
      try {
        const result = await client.getCustomImportSchemas({});
        setCustomSchemas(result.schemas);
      } catch (error) {
        console.error(error);
      }
    };

    void loadSchemas();
  }, [open, client]);

  const searchProducts = useHandler(async (sku: string) => {
    try {
      return await merchantClient.searchProducts({
        searchString: sku,
        provider: Provider.SHOPIFY,
        first: 2,
      });
    } catch (error) {
      console.error(error);
      return { products: [] };
    }
  });

  const importOrders = useHandler(async () => {
    if (!file || !selectedSource) {
      return;
    }

    setImportLoading(true);

    const rawText = await file.text();
    const rawRows = rawText.split("\n").slice(0, 10);
    datadogLogs.logger.info("CSV import file received", {
      fileName: file.name,
      fileSize: file.size,
      rawRows,
    });

    let columnMap: FieldToColumnMap | undefined;
    let parseResult;

    if (selectedSource.type === "custom") {
      parseResult = await parseCustomImportCsv(
        file,
        selectedSource.schema.columnMapping,
        searchProducts,
      );
      columnMap = selectedSource.schema.columnMapping.fieldMappings;
    } else {
      const importSource = selectedSource.source;
      // tslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check -- TODO: grandfather FIXME
      switch (importSource) {
        case FulfillmentImportSource.APPAREL_MAGIC:
          parseResult = await parseApparelMagicCsv(file);
          columnMap = apparelMagicColumnMap;
          break;
        case FulfillmentImportSource.FORTY_BOXES:
          parseResult = await parseFortyBoxesCsv(file, searchProducts);
          columnMap = fortyBoxesColumnMap;
          break;
        case FulfillmentImportSource.KICKSTARTER:
          parseResult = await parseKickstarterCsv(file, searchProducts);
          columnMap = kickstarterColumnMap;
          break;
        case FulfillmentImportSource.ADULTS_AND_CRAFTS:
          parseResult = await parseAdultsAndCraftsCsv(file, searchProducts);
          columnMap = adultsAndCraftsColumnMap;
          break;
        default:
          toast(
            `Import not yet supported for ${getFulfillmentInputSourceDisplayName(importSource)}`,
            { variant: "error" },
          );
          setImportLoading(false);
          return;
      }
    }

    const importSource =
      selectedSource.type === "custom"
        ? selectedSource.schema.name
        : selectedSource.source;

    const { orders: fulfillmentOrders, parseErrors } = parseResult;

    if (parseErrors.length > 0) {
      datadogLogs.logger.warn("CSV import parse errors", {
        importSource,
        parseErrorCount: parseErrors.length,
        parseErrors: parseErrors.map((e) => ({
          orderIdentifier: e.orderIdentifier,
          message: e.message,
        })),
      });
      toast(formatParseErrors(parseErrors), { variant: "error" });
      if (fulfillmentOrders.length === 0) {
        setImportLoading(false);
        return;
      }
    }

    const validation = validateFulfillmentOrders(fulfillmentOrders, columnMap);
    if (!validation.valid) {
      datadogLogs.logger.warn("CSV import validation errors", {
        importSource,
        validationErrorCount: validation.errors.length,
        validationErrors: validation.errors.map((e) => ({
          orderNumber: e.orderNumber,
          messages: e.messages,
        })),
      });
      toast(formatValidationErrors(validation.errors), { variant: "error" });
      setImportLoading(false);
      return;
    }

    try {
      await client.createManualFulfillmentGroups({ fulfillmentOrders });
      datadogLogs.logger.info("CSV import success", {
        importSource,
        orderCount: fulfillmentOrders.length,
      });
      toast("Imported orders. Refreshing...", { variant: "success" });
    } catch (error) {
      datadogLogs.logger.error("CSV import RPC failed", {
        importSource,
        orderCount: fulfillmentOrders.length,
        error: error instanceof Error ? error.message : String(error),
      });
      console.error(error);
      toast("Failed to import orders", { variant: "error" });
    } finally {
      await triggerReload();
      setImportLoading(false);
      setOpen(false);
    }
  });

  const [file, setFile] = useState<File | null>(null);

  const title = selectedSource
    ? selectedSource.type === "builtin"
      ? `Import orders from ${getFulfillmentInputSourceDisplayName(selectedSource.source)}`
      : `Import orders — ${selectedSource.schema.name}`
    : "Import orders";

  const handleFilesAdded = useHandler((newFiles: FileList | null) => {
    if (newFiles && newFiles.length > 0) {
      // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
      setFile(newFiles[0]);
    }
  });

  const handleFileDeleted = useHandler(() => {
    setFile(null);
  });

  const downloadExample = useHandler(async () => {
    if (!selectedSource) {
      return;
    }

    if (selectedSource.type === "custom") {
      const example = getCustomSchemaCSVExample(
        selectedSource.schema.columnMapping,
      );
      const csvRows: string[][] = [
        example.headers,
        ...example.rows.map((row) =>
          example.headers.map((header) => row[header] ?? ""),
        ),
      ];
      const csvContent = csvRows
        .map((row) => row.map(csvEscapeField).join(","))
        .join("\n");
      const safeName = selectedSource.schema.name
        .toLowerCase()
        .replace(/\s+/g, "-");
      downloadCSV(csvContent, `${safeName}-example.csv`);
      return;
    }

    const example = getCSVExample(selectedSource.source);
    const csvContent = generateCSVContent(example);
    const sourceName = getFulfillmentInputSourceDisplayName(
      selectedSource.source,
    );
    const filename = `${sourceName.toLowerCase().replace(/\s+/g, "-")}-example.csv`;

    downloadCSV(csvContent, filename);
  });

  return (
    <RedoBaseModal
      isOpen={open}
      onModalCloseRequested={() => {
        setOpen(false);
      }}
    >
      <RedoModalHeader cancelClicked={cancelImport} title={title} />
      {selectedSource ? (
        <Flex
          align="center"
          dir="column"
          grow={1}
          justify="center"
          pb="xl"
          px="3xl"
          w="full"
        >
          <RedoFileUpload
            accept=".csv,.xls,.xlsx"
            className={importOrdersModalCss.fileUpload}
            files={file ? [file] : []}
            handleFileDeleted={handleFileDeleted}
            handleFilesAdded={handleFilesAdded}
            nFilesToAccept={1}
          />
          <Flex dir="row" gap="md" py="md" w="full">
            <RedoButton
              hierarchy="tertiary"
              onClick={downloadExample}
              size="sm"
              text="Download Example"
              theme="normal"
            />
          </Flex>
          <Flex dir="row" gap="md" w="full">
            <RedoButton
              className={classNames(importOrdersModalCss.modalButton)}
              hierarchy="secondary"
              onClick={cancelImport}
              size="md"
              text="Cancel"
              theme="normal"
            />
            <RedoButton
              className={classNames(importOrdersModalCss.modalButton)}
              disabled={!file}
              hierarchy="primary"
              onClick={importOrders}
              pending={importLoading}
              size="md"
              text="Import"
              theme="normal"
            />
          </Flex>
        </Flex>
      ) : (
        <ImportSourceButtons
          customSchemas={customSchemas}
          setSelectedSource={setSelectedSource}
        />
      )}
    </RedoBaseModal>
  );
});

const ImportSourceButton = memo(function InputSourceButton({
  inputSource,
  onClick,
}: {
  inputSource: FulfillmentImportSource;
  onClick: () => void;
}) {
  return (
    <Flex flex={1} w="full">
      <RedoButton
        className={classNames(importOrdersModalCss.modalButton)}
        hierarchy="secondary"
        IconTrailing={ArrowRightIcon}
        onClick={onClick}
        size="md"
        text={getFulfillmentInputSourceDisplayName(inputSource)}
        theme="normal"
      />
    </Flex>
  );
});

const CustomSchemaButton = memo(function CustomSchemaButton({
  schema,
  onClick,
}: {
  schema: CustomOrderImportSchema;
  onClick: () => void;
}) {
  return (
    <Flex flex={1} w="full">
      <RedoButton
        className={classNames(importOrdersModalCss.modalButton)}
        hierarchy="secondary"
        IconTrailing={ArrowRightIcon}
        onClick={onClick}
        size="md"
        text={schema.name}
        theme="normal"
      />
    </Flex>
  );
});

const ImportSourceButtons = memo(function InputSourceButtons({
  setSelectedSource,
  customSchemas,
}: {
  setSelectedSource: (source: SelectedImportSource) => void;
  customSchemas: CustomOrderImportSchema[];
}) {
  const team = useContext(TeamContext);

  const onChooseApparelMagic = useHandler(() => {
    setSelectedSource({
      type: "builtin",
      source: FulfillmentImportSource.APPAREL_MAGIC,
    });
  });

  const onChooseFortyBoxes = useHandler(() => {
    setSelectedSource({
      type: "builtin",
      source: FulfillmentImportSource.FORTY_BOXES,
    });
  });

  const onChooseKickstarter = useHandler(() => {
    setSelectedSource({
      type: "builtin",
      source: FulfillmentImportSource.KICKSTARTER,
    });
  });

  const onChooseAdultsAndCrafts = useHandler(() => {
    setSelectedSource({
      type: "builtin",
      source: FulfillmentImportSource.ADULTS_AND_CRAFTS,
    });
  });

  const navigate = useNavigate();

  const showAdultsAndCrafts =
    team?._id === TYSON_DEV_3_TEAM_ID ||
    team?._id === ADULTS_AND_CRAFTS_TEAM_ID;

  const navigateToCustomSchemas = useHandler(() => {
    if (team?._id) {
      navigate(`/stores/${team._id}/settings/oms/custom-import-schemas`);
    }
  });

  return (
    <Flex dir="column" gap="md" grow={1} pb="xl" pt="none" px="3xl">
      <Text fontSize="sm">Where are you importing orders from?</Text>
      <Flex dir="column" gap="2xl">
        <Flex dir="column" gap="md">
          <ImportSourceButton
            inputSource={FulfillmentImportSource.APPAREL_MAGIC}
            onClick={onChooseApparelMagic}
          />
          <ImportSourceButton
            inputSource={FulfillmentImportSource.FORTY_BOXES}
            onClick={onChooseFortyBoxes}
          />
          <ImportSourceButton
            inputSource={FulfillmentImportSource.KICKSTARTER}
            onClick={onChooseKickstarter}
          />
          {showAdultsAndCrafts && (
            <ImportSourceButton
              inputSource={FulfillmentImportSource.ADULTS_AND_CRAFTS}
              onClick={onChooseAdultsAndCrafts}
            />
          )}
        </Flex>

        {customSchemas.length > 0 && (
          <Flex
            borderColor="primary"
            borderStyle="solid"
            borderTopWidth="1px"
            dir="column"
            gap="md"
            pt="md"
          >
            <Text fontSize="xs" fontWeight="medium" textColor="secondary">
              Custom schemas
            </Text>
            {customSchemas.map((schema) => (
              <CustomSchemaButton
                key={schema.id}
                onClick={() => setSelectedSource({ type: "custom", schema })}
                schema={schema}
              />
            ))}
          </Flex>
        )}

        <Flex dir="row" justify="flex-end">
          <RedoButton
            hierarchy="tertiary"
            onClick={navigateToCustomSchemas}
            size="sm"
            text="Manage custom schemas"
            theme="normal"
          />
        </Flex>
      </Flex>
    </Flex>
  );
});
